// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  SimpleGrid,
  Paper,
  Text,
  Group,
  Badge,
  Collapse,
  Box,
  Loader,
  Center,
  Divider,
  Table,
} from '@mantine/core';
import {
  IconTemplate,
  IconChevronDown,
  IconChevronRight,
  IconClock,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useScheduleTemplates } from '../../hooks/useScheduleTemplates';

interface TemplateShift {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  position: string;
}

interface ScheduleTemplate {
  id: string;
  name: string;
  description: string;
  shiftCount: number;
  shifts?: TemplateShift[];
  createdAt: string;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function ScheduleTemplatesPage() {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: rawQueryData, loading: isLoading } = useScheduleTemplates();
  const queryData = rawQueryData as any;
  const templates = (queryData?.scheduleTemplates ?? []) as ScheduleTemplate[];

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="Schedule Templates"
        subtitle="Reusable schedule patterns for quick shift assignment"
      />

      {isLoading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : !templates || templates.length === 0 ? (
        <EmptyState
          icon={<IconTemplate size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
          title="No Templates"
          message="Schedule templates allow you to quickly apply recurring shift patterns."
        />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {templates.map((template) => (
            <Paper
              key={template.id}
              p="md"
              withBorder
              style={{ cursor: 'pointer' }}
              onClick={() => toggleExpand(template.id)}
            >
              <Group justify="space-between" mb="xs">
                <Group gap="sm">
                  <IconTemplate size={20} />
                  <Text fw={600} size="sm">
                    {template.name}
                  </Text>
                </Group>
                {expandedId === template.id ? (
                  <IconChevronDown size={16} />
                ) : (
                  <IconChevronRight size={16} />
                )}
              </Group>

              {template.description && (
                <Text size="sm" c="dimmed" mb="xs" lineClamp={2}>
                  {template.description}
                </Text>
              )}

              <Group gap="sm">
                <Badge variant="light" size="sm" icon={<IconClock size={12} />}>
                  {template.shiftCount} shift{template.shiftCount !== 1 ? 's' : ''}
                </Badge>
              </Group>

              <Collapse in={expandedId === template.id}>
                <Divider my="sm" />
                <Box>
                  {template.shifts && template.shifts.length > 0 ? (
                    <Table withRowBorders={false} horizontalSpacing="xs" verticalSpacing={4}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>
                            <Text size="xs" fw={600}>Day</Text>
                          </Table.Th>
                          <Table.Th>
                            <Text size="xs" fw={600}>Time</Text>
                          </Table.Th>
                          <Table.Th>
                            <Text size="xs" fw={600}>Position</Text>
                          </Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {template.shifts.map((shift, idx) => (
                          <Table.Tr key={idx}>
                            <Table.Td>
                              <Text size="xs">
                                {DAY_NAMES[shift.dayOfWeek] || `Day ${shift.dayOfWeek}`}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs">
                                {shift.startTime} - {shift.endTime}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs">{shift.position || '-'}</Text>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  ) : (
                    <Text size="xs" c="dimmed" ta="center" py="xs">
                      No shift details available
                    </Text>
                  )}
                </Box>
              </Collapse>
            </Paper>
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}
